import { createContext, useContext } from "react";

import { useScheduleController } from "../../../features/planning/schedule/state/useScheduleController";

/**
 * Schedule page state + handlers (data load, timeline, resize, drag-move, create flows).
 * Implementation lives in {@link useScheduleController} under `features/planning/schedule/state/`.
 *
 * @typedef {import('../../../features/planning/schedule/state/scheduleContextValue').ScheduleContextValue} ScheduleContextValue
 */

/** @type {import('react').Context<ScheduleContextValue|null>} */
export const ScheduleContext = createContext(null);

/**
 * Read schedule page state and handlers from {@link ScheduleProvider}.
 *
 * @returns {ScheduleContextValue}
 * @throws {Error} When used outside `ScheduleProvider`.
 */
export function useScheduleContext() {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error(
      "useScheduleContext must be used within a ScheduleProvider"
    );
  }
  return context;
}

/**
 * Wrap `/planning/schedule` (and any subtree that calls {@link useScheduleContext}).
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @returns {JSX.Element}
 */
export function ScheduleProvider({ children }) {
  const value = useScheduleController();

  return (
    <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
  );
}

export default ScheduleProvider;
